import GUI from 'lil-gui'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

import waterVertexShader from './shaders/vertex.glsl'
import waterFragmentShader from './shaders/fragment.glsl'

/**
 * Base
 */
// Debug
const gui = new GUI({
    width: 220
})
gui.close()
gui.hide()
const debugObject = {}

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Loaders
 */
// Texture loader
const textureLoader = new THREE.TextureLoader()
const texture = textureLoader.load('baked.jpg')
texture.flipY = false
texture.colorSpace = THREE.SRGBColorSpace

// GLTF loader
const gltfLoader = new GLTFLoader()

/**
 * Materials
 */

debugObject.skyScreenColor = '#94d1ff'
debugObject.skySunColor = '#fffbcc'
debugObject.sunColor = '#ffddbd'
debugObject.waterSpeed = 0.2
debugObject.waterScale = 8
debugObject.colorStart = '#618dbd'
debugObject.colorEnd = '#b3e5ff'

const bakedMaterial = new THREE.MeshBasicMaterial({map:texture})
const skyScreenMaterial = new THREE.MeshBasicMaterial({ color: debugObject.skyScreenColor })
const skySunMaterial = new THREE.MeshBasicMaterial({color: debugObject.skySunColor })
const sunMaterial = new THREE.MeshBasicMaterial({color: debugObject.sunColor })

// water material
const waterMaterial = new THREE.ShaderMaterial({
    uniforms:
    {
        uTime: { value : 0 },
        uSpeed: { value : debugObject.waterSpeed },
        uScale: { value : debugObject.waterScale },
        uColorStart: { value : new THREE.Color(debugObject.colorStart)},
        uColorEnd: { value : new THREE.Color(debugObject.colorEnd)}
    },
    vertexShader: waterVertexShader,
    fragmentShader: waterFragmentShader
})
gui.add(waterMaterial.uniforms.uSpeed, 'value', 0.01, 0.5, 0.01).name('waterSpeed')
gui.add(waterMaterial.uniforms.uScale, 'value', 3, 20, 0.01).name('waterScale')
gui.addColor(debugObject, 'colorStart')
    .onChange(()=>{waterMaterial.uniforms.uColorStart.value.set(debugObject.colorStart)})
gui.addColor(debugObject, 'colorEnd')
    .onChange(()=>{waterMaterial.uniforms.uColorEnd.value.set(debugObject.colorEnd)})

/**
 * Model
 */

gltfLoader.load(
    'solarSystem.glb',
    (model)=>
    {
        // Set baked material for the model

        // Find Lights
        const joinedMesh = model.scene.children.find((child) => child.name === 'joined' )
        const skyScreenMesh = model.scene.children.find((child) => child.name === 'skyScreen' )
        const skySunMesh = model.scene.children.find((child) => child.name === 'skySun' )
        const sunMesh = model.scene.children.find((child) => child.name === 'sun' )
        const waterMesh = model.scene.children.find((child) => child.name === 'water' )

        // Apply materials to the Lights
        joinedMesh.material = bakedMaterial
        skyScreenMesh.material = skyScreenMaterial
        skySunMesh.material = skySunMaterial
        sunMesh.material = sunMaterial
        waterMesh.material = waterMaterial

        // Debug colors
        gui
            .addColor(debugObject, 'skyScreenColor')
            .onChange(()=>{skyScreenMesh.material.color.set(debugObject.skyScreenColor)})
        gui
            .addColor(debugObject, 'skySunColor')
            .onChange(()=>{skySunMesh.material.color.set(debugObject.skySunColor)})
        gui
            .addColor(debugObject, 'sunColor')
            .onChange(()=>{sunMesh.material.color.set(debugObject.sunColor)})

        scene.add(model.scene)
    }
)

/**
 * Environment
 */

const space = textureLoader.load('space.jpg')
space.mapping = THREE.EquirectangularReflectionMapping
space.colorSpace = THREE.SRGBColorSpace
scene.background = space
// scene.environment = space

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 20000)
camera.position.x = 0
// camera.position.y = 100 // 0.15
// camera.position.z = 100 // 0.8
camera.position.y = 0.15
camera.position.z = 0.8
scene.add(camera)
gui.add(camera.position, 'y', 0, 0.2, 0.001).name('cameraHeight')

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

// Set the limits for up/down tilt
controls.minPolarAngle = Math.PI / 2 - Math.PI / 5; // 45 degrees upwards
controls.maxPolarAngle = Math.PI / 2 + Math.PI / 5; // 45 degrees downwards



/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    // Update vertex shader
    waterMaterial.uniforms.uTime.value = elapsedTime

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()